import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { fmtUSD, fmtECU } from "../lib/format";
import { useBilling } from "../context/BillingContext";
import {
  PageHeader, Panel, DataTable, DsButton, SpendChart, Chip,
} from "../ds";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function fmtDubai(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Dubai",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch { return iso; }
}

function ManualEntryForm({ projects }) {
  const qc = useQueryClient();
  const [source, setSource] = useState("subscription");
  const [projectId, setProjectId] = useState(projects?.[0]?.id || "");
  const [usd, setUsd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!projectId || !usd) return toast.error("Project and USD required.");
    setBusy(true);
    try {
      await api.post("/settings/cost-entries", {
        source, project_id: projectId, usd: Number(usd), note,
      });
      setUsd(""); setNote("");
      qc.invalidateQueries({ queryKey: ["costs"] });
      qc.invalidateQueries({ queryKey: ["cost-entries"] });
      qc.invalidateQueries({ queryKey: ["billing"] });
      toast.success("Entry added.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const selCls = "h-10 w-full rounded-[5px] border border-[color:var(--line-2)] bg-[color:var(--panel-2)] px-3 text-sm text-[color:var(--fg)]";
  return (
    <div className="grid grid-cols-5 gap-3 items-end" data-testid="manual-entry-form">
      <div>
        <Label>Source</Label>
        <select className={selCls} value={source} onChange={(e)=>setSource(e.target.value)} data-testid="me-source">
          <option value="subscription">subscription</option>
          <option value="emergent">emergent</option>
          <option value="claude_api">claude_api</option>
          <option value="other">other</option>
        </select>
      </div>
      <div>
        <Label>Project</Label>
        <select className={selCls} value={projectId} onChange={(e)=>setProjectId(e.target.value)} data-testid="me-project">
          {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <Label>USD</Label>
        <Input type="number" step="0.01" value={usd} onChange={(e)=>setUsd(e.target.value)}
               className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
               data-testid="me-usd" />
      </div>
      <div className="col-span-1">
        <Label>Note</Label>
        <Input value={note} onChange={(e)=>setNote(e.target.value)}
               className="bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
               data-testid="me-note" />
      </div>
      <DsButton variant="primary" onClick={submit} disabled={busy} data-testid="me-submit">
        Add entry
      </DsButton>
    </div>
  );
}

function ReverseCell({ entry, onReversed }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (entry.is_reversal) {
    return <span className="font-mono text-[10.5px] uppercase text-[color:var(--muted)]">reversal</span>;
  }
  if (entry.reversed_by) {
    return <Chip value="rejected" label="reversed" testId={`entry-${entry.id}-reversed`} />;
  }
  const doReverse = async () => {
    setBusy(true);
    try {
      await api.post(`/settings/cost-entries/${entry.id}/reverse`, { note: "" });
      toast.success("Entry reversed.");
      onReversed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid={`entry-${entry.id}-confirm-row`}>
        <DsButton variant="no" onClick={doReverse} disabled={busy} data-testid={`entry-${entry.id}-confirm`}>
          {busy ? "…" : "Confirm"}
        </DsButton>
        <DsButton variant="ghost" onClick={() => setConfirming(false)} data-testid={`entry-${entry.id}-cancel`}>
          Cancel
        </DsButton>
      </span>
    );
  }
  return (
    <DsButton onClick={() => setConfirming(true)} data-testid={`entry-${entry.id}-reverse`}>
      Reverse
    </DsButton>
  );
}

export default function Costs() {
  const qc = useQueryClient();
  const [by, setBy] = useState("project");
  const [window, setWindow] = useState("month");
  useBilling(); // rate pill rendered via billingData below

  const { data } = useQuery({
    queryKey: ["costs", by, window],
    queryFn: async () => (await api.get("/costs", { params: { by, window } })).data,
    refetchInterval: 30000,
  });
  const { data: billingData } = useQuery({
    queryKey: ["billing"],
    queryFn: async () => (await api.get("/settings/billing")).data,
  });
  const { data: projectsData } = useQuery({
    queryKey: ["projects-mini"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const { data: ledgerData } = useQuery({
    queryKey: ["cost-entries", window],
    queryFn: async () => (await api.get("/costs/entries",
      { params: { window, limit: 100 } })).data,
  });

  const projectsList = projectsData?.projects || [];

  const rows = data?.rows || [];
  const series = useMemo(
    // Total = every row's USD, counted exactly once. Emergent rows already
    // carry their USD value in `s.usd` (= s.emergent_usd + s.claude_usd + ...).
    // Previously the chart added `s.ecu * usdPerEcu` on top, which double-
    // counted Emergent spend.
    () => (data?.series || []).map((s) => ({
      day: s.day,
      total: Number(s.usd || 0),
      claude: Number(s.claude_usd || 0),
    })),
    [data]
  );
  const purchases = billingData?.purchases || [];
  const rate = billingData?.usd_per_ecu;
  const entries = ledgerData?.entries || [];

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6">
      <PageHeader
        title="Costs"
        subtitle="Claude in USD, Emergent in ECU — Asia/Dubai time"
      />

      <div className="flex items-center gap-2" data-testid="costs-controls">
        <DsButton
          variant={by === "project" ? "primary" : "default"}
          onClick={() => setBy("project")}
          data-testid="costs-by-project"
        >
          By project
        </DsButton>
        <DsButton
          variant={by === "agent" ? "primary" : "default"}
          onClick={() => setBy("agent")}
          data-testid="costs-by-agent"
        >
          By agent
        </DsButton>
        <span className="w-px h-5 bg-[color:var(--line)] mx-1" />
        <DsButton
          variant={window === "today" ? "primary" : "default"}
          onClick={() => setWindow("today")}
          data-testid="costs-win-today"
        >
          Today
        </DsButton>
        <DsButton
          variant={window === "month" ? "primary" : "default"}
          onClick={() => setWindow("month")}
          data-testid="costs-win-month"
        >
          This month
        </DsButton>
      </div>

      <Panel
        label={window === "today" ? "SPEND · TODAY" : "SPEND · LAST 30 DAYS"}
        action={<span className="font-mono text-[10.5px] text-[color:var(--muted)]">USD</span>}
        testId="costs-spend-panel"
      >
        <SpendChart data={series} height={240} testId="costs-chart" />
      </Panel>

      <Panel label="ADD MANUAL ENTRY" testId="costs-manual-panel">
        <ManualEntryForm projects={projectsList} />
      </Panel>

      <Panel
        label={`BY ${by === "project" ? "PROJECT" : "AGENT"}`}
        testId="costs-rows-panel"
      >
        <DataTable
          testId="costs-table"
          columns={[
            { key: "label", label: by === "project" ? "Project" : "Agent",
              render: (r) => {
                const italic = r.group === "ceo_emergent" || r.group === "ceo_manual";
                const node = r.slug ? (
                  <Link to={`/projects/${r.slug}`}
                        className="text-[color:var(--fg)] hover:text-[color:var(--cobalt)]"
                        data-testid={`costs-project-link-${r.slug}`}>
                    {r.label}
                  </Link>
                ) : (
                  <span data-testid={`costs-row-label-${r.key}`}>{r.label}</span>
                );
                return italic
                  ? <span className="italic text-[color:var(--muted)]">{node}</span>
                  : node;
              } },
            { key: "runs", label: "Runs", right: true, width: 90,
              render: (r) => r.runs },
            { key: "claude_usd", label: "Claude (USD)", right: true, width: 140,
              render: (r) => Number(r.claude_usd || 0) > 0
                ? fmtUSD(r.claude_usd) : <span className="text-[color:var(--muted)]">—</span> },
            { key: "emergent_ecu", label: "Emergent (ECU)", right: true, width: 150,
              render: (r) => Number(r.emergent_ecu || 0) > 0
                ? fmtECU(r.emergent_ecu) : <span className="text-[color:var(--muted)]">—</span> },
            { key: "emergent_usd", label: "Emergent (USD)", right: true, width: 150,
              render: (r) => Number(r.emergent_usd || 0) > 0
                ? fmtUSD(r.emergent_usd) : <span className="text-[color:var(--muted)]">—</span> },
            { key: "other_usd", label: "Other (USD)", right: true, width: 130,
              render: (r) => {
                const v = Number(r.other_usd || 0) + Number(r.manual_usd || 0);
                return v > 0 ? fmtUSD(v) : <span className="text-[color:var(--muted)]">—</span>;
              } },
            { key: "total_usd", label: "Total (USD)", right: true, width: 140,
              render: (r) => <span className="font-mono">{fmtUSD(r.total_usd)}</span> },
          ]}
          rows={rows.map((r) => ({ ...r, id: r.key }))}
          empty="No activity in this window."
        />
      </Panel>

      <Panel
        label="COST LEDGER"
        action={
          <span className="font-mono text-[10.5px] uppercase text-[color:var(--muted)]">
            {entries.length} entries · {window === "today" ? "today" : "this month"}
          </span>
        }
        testId="costs-ledger-panel"
      >
        <DataTable
          testId="costs-ledger-table"
          columns={[
            { key: "occurred_at", label: "Date", mono: true, width: 170,
              render: (e) => fmtDubai(e.occurred_at) },
            { key: "project", label: "Project", width: 160,
              render: (e) => (
                e.project_slug ? (
                  <Link to={`/projects/${e.project_slug}`}
                        className="text-[color:var(--fg)] hover:text-[color:var(--cobalt)]"
                        data-testid={`ledger-project-${e.project_slug}`}>
                    {e.project_name}
                  </Link>
                ) : <span className="text-[color:var(--muted)]">—</span>
              ) },
            { key: "source", label: "Source", width: 130,
              render: (e) => <Chip value={e.source} /> },
            { key: "task", label: "Task", width: 100,
              render: (e) => e.task_id ? (
                <Link to={`/tasks/${e.task_id}`}
                      className="font-mono text-xs text-[color:var(--cobalt)] hover:brightness-125">
                  T-{String(e.task_id).slice(-3)}
                </Link>
              ) : <span className="text-[color:var(--muted)]">—</span> },
            { key: "ecu", label: "ECU", right: true, width: 100,
              render: (e) => e.ecu != null ? Number(e.ecu).toFixed(2) : "—" },
            { key: "usd", label: "USD", right: true, width: 110,
              render: (e) => (
                <span className={e.is_reversal ? "text-[color:var(--warn)]" : "text-[color:var(--fg)]"}>
                  {fmtUSD(e.usd)}
                </span>
              ) },
            { key: "method", label: "Method", width: 130,
              render: (e) => (
                <span className="font-mono text-[10.5px] uppercase text-[color:var(--muted)]">
                  {e.method}
                </span>
              ) },
            { key: "actions", label: "", right: true, width: 170,
              render: (e) => (
                <ReverseCell
                  entry={e}
                  onReversed={() => {
                    qc.invalidateQueries({ queryKey: ["costs"] });
                    qc.invalidateQueries({ queryKey: ["cost-entries"] });
                    qc.invalidateQueries({ queryKey: ["billing"] });
                  }}
                />
              ) },
          ]}
          rows={entries}
          empty="No cost entries in this window."
        />
      </Panel>

      <Panel
        label="CREDIT PURCHASES"
        action={
          <span className="font-mono text-[10.5px] text-[color:var(--muted)]" data-testid="costs-rate-pill">
            RATE ${rate != null ? Number(rate).toFixed(4) : "—"} / ECU
          </span>
        }
        testId="purchases-panel"
      >
        <DataTable
          testId="purchases-table"
          columns={[
            { key: "date", label: "Date", mono: true, width: 140,
              render: (r) => r.date },
            { key: "usd", label: "USD", right: true, width: 140,
              render: (r) => fmtUSD(r.usd) },
            { key: "ecu", label: "ECU", right: true, width: 140,
              render: (r) => fmtECU(r.ecu) },
            { key: "note", label: "Note",
              render: (r) => r.note || "—" },
          ]}
          rows={purchases}
          empty="No purchases recorded yet."
        />
      </Panel>
    </div>
  );
}
